/**
 * Web-only stylesheet injection for the Skeleton animations.
 *
 * The animations are CSS rather than JS-driven: a list of thirty placeholders
 * costs nothing to animate off the main thread, and it keeps Reanimated (whose
 * worklet runtime is stubbed out in Storybook) off the web bundle's path.
 *
 * Rules are keyed off `data-*` attributes because `dataSet` is the only
 * escape hatch react-native-web gives us for reaching CSS. The sheen colour
 * comes from the theme, so one gradient rule is injected per colour actually
 * used — at most one per theme.
 */

/** `data-a3s-skeleton-animation="pulse"` — set on the placeholder surface. */
export const PULSE_ATTRIBUTE = "a3sSkeletonAnimation";

/** `data-a3s-skeleton-wave="<colour>"` — set on the travelling sheen. */
export const WAVE_ATTRIBUTE = "a3sSkeletonWave";

const STYLE_ELEMENT_ID = "a3s-skeleton-styles";

const BASE_RULES = [
  "@keyframes a3s-skeleton-pulse{0%{opacity:1}50%{opacity:0.4}100%{opacity:1}}",
  // Rests at the right edge for the second half of the cycle, which reads as a
  // pause between passes instead of a hard snap back.
  "@keyframes a3s-skeleton-wave{0%{transform:translateX(-100%)}50%{transform:translateX(100%)}100%{transform:translateX(100%)}}",
  '[data-a3s-skeleton-animation="pulse"]{animation:a3s-skeleton-pulse 2s ease-in-out 0.5s infinite}',
  "[data-a3s-skeleton-wave]{animation:a3s-skeleton-wave 2s linear 0.5s infinite}",
  // The sheen has to be hidden outright, not just halted: a stopped sheen sits
  // untranslated over the middle of the surface, which would leave
  // reduced-motion users with a permanent bright band instead of a plain
  // placeholder.
  "@media (prefers-reduced-motion: reduce){" +
    '[data-a3s-skeleton-animation="pulse"]{animation:none}' +
    "[data-a3s-skeleton-wave]{animation:none;display:none}" +
    "}",
];

/**
 * Colours are interpolated straight into a stylesheet, so only accept the hex
 * and rgb(a) forms the theme actually produces. Anything else is dropped rather
 * than trusted.
 */
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.,\s%/]+\))$/i;

let styleElement: HTMLStyleElement | undefined;
const injectedRules = new Set<string>();

function getStyleElement(): HTMLStyleElement | undefined {
  if (typeof document === "undefined") return undefined;
  if (styleElement?.isConnected === true) return styleElement;

  const element = document.createElement("style");
  element.id = STYLE_ELEMENT_ID;
  document.head.appendChild(element);
  styleElement = element;
  return element;
}

function appendRule(rule: string): void {
  if (injectedRules.has(rule)) return;
  const element = getStyleElement();
  if (element === undefined) return;
  element.appendChild(document.createTextNode(rule));
  injectedRules.add(rule);
}

/**
 * Ensures the keyframes and animation rules exist in the document.
 *
 * Idempotent, and safe to call from any number of Skeleton instances.
 */
export function injectSkeletonBaseStyles(): void {
  for (const rule of BASE_RULES) appendRule(rule);
}

/**
 * Ensures the sheen gradient for `highlightColor` exists in the document.
 *
 * Returns silently for colour formats it can't vouch for, in which case the
 * wave renders without a gradient rather than injecting unvalidated CSS.
 */
export function injectSkeletonWaveGradient(highlightColor: string): void {
  if (!SAFE_COLOR.test(highlightColor)) return;
  appendRule(
    `[data-a3s-skeleton-wave="${highlightColor}"]` +
      `{background-image:linear-gradient(90deg,transparent,${highlightColor},transparent)}`,
  );
}
